import { ElementAddress } from 'editor/src/store/design/types';

export interface WarningsState {
  list: Warning[];
  variantWarnings: { [key: string]: Warning[] };
}

export type ElementWarning =
  | DPIWarning
  | NotVisibleWarning
  | PartlyOutsideWarning
  | OutOfRangeWarning
  | SampleElementNotReplacedWarning
  | DtfImageBackgroundWarning
  | MaxCharCountReachedWarning
  | OutsideSheetWarning
  | TextOverflowWarning
  | SemiTransparentPixelsWarning
  | FineDetailWarning
  | SmallTextWarning
  | ThinLineWarning
  | GradientWarning;
export type GlobalWarning = EmptyDesignWarning | WhiteUnderbaseWarning;
export type SpreadWarning = MaxStitchCountWarning | MaxColorCountWarning | EmptyPageWarning | EmptyImageBoxWarning;
export type Warning = GlobalWarning | ElementWarning | SpreadWarning;

export function isElementWarning(warning: Warning): warning is ElementWarning {
  return elementWarningTypeSet.has(warning.type);
}

export function isGlobalWarning(warning: Warning): warning is GlobalWarning {
  return globalWarningTypeSet.has(warning.type);
}

export function isSpreadWarning(warning: Warning): warning is SpreadWarning {
  return spreadWarningTypeSet.has(warning.type);
}

export enum WarningLevel {
  Lowest = 'Lowest',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

interface ElementWarningBase {
  type: WarningType;
  uuid: number;
  spreadIndex: number;
  level: WarningLevel;
  isProceedBlocked?: boolean;
}

interface SpreadWarningBase {
  type: WarningType;
  spreadIndex: number;
  level: WarningLevel;
  isProceedBlocked?: boolean;
}

export interface DPIWarning extends ElementWarningBase {
  type: WarningType.LowDPI;
  dpi: number;
}

export interface NotVisibleWarning extends ElementWarningBase {
  type: WarningType.NotVisible;
}

export interface PartlyOutsideWarning extends ElementWarningBase {
  type: WarningType.PartlyOutside;
}

export interface OutsideSheetWarning extends ElementWarningBase {
  type: WarningType.OutsideSheet;
}

export interface DtfImageBackgroundWarning extends ElementWarningBase {
  type: WarningType.DtfImageBackground;
}

export interface SemiTransparentPixelsWarning extends ElementWarningBase {
  type: WarningType.SemiTransparentPixels;
}

export interface FineDetailWarning extends ElementWarningBase {
  type: WarningType.FineDetail;
  minLineWidthMm: number;
}

export interface SmallTextWarning extends ElementWarningBase {
  type: WarningType.SmallText;
  textHeightMm: number;
  minTextHeightMm: number;
}

export interface ThinLineWarning extends ElementWarningBase {
  type: WarningType.ThinLine;
  lineWidthMm: number;
  minLineWidthMm: number;
}

export interface GradientWarning extends ElementWarningBase {
  type: WarningType.Gradient;
}

export interface MaxCharCountReachedWarning extends ElementWarningBase {
  type: WarningType.MaxCharCountReached;
}

export interface TextOverflowWarning extends ElementWarningBase {
  type: WarningType.TextOverflow;
}

export interface OutOfRangeWarning extends ElementWarningBase {
  type: WarningType.OutOfRange;
}

export interface SampleElementNotReplacedWarning extends ElementWarningBase {
  type: WarningType.SampleElementNotReplaced;
}

export interface MaxStitchCountWarning extends SpreadWarningBase {
  type: WarningType.MaxStitchCount;
  count: number;
}
export interface MaxColorCountWarning extends SpreadWarningBase {
  type: WarningType.MaxColorCount;
  count: number;
}

export interface EmptyPageWarning extends SpreadWarningBase {
  type: WarningType.EmptyPage;
}

export interface EmptyDesignWarning {
  type: WarningType.EmptyDesign;
  level: WarningLevel;
  isProceedBlocked?: boolean;
}

export interface WhiteUnderbaseWarning {
  type: WarningType.WhiteUnderbase;
  level: WarningLevel;
  isProceedBlocked?: boolean;
}

export interface EmptyImageBoxWarning extends SpreadWarningBase {
  type: WarningType.EmptyImageBox;
}

export enum WarningType {
  LowDPI = 'LowDPI',
  NotVisible = 'NotVisible',
  OutOfRange = 'OutOfRange',
  MaxStitchCount = 'MaxStitchCount',
  MaxColorCount = 'MaxColorCount',
  SampleElementNotReplaced = 'SampleElementNotReplaced',
  EmptyPage = 'EmptyPage',
  EmptyDesign = 'EmptyDesign',
  WhiteUnderbase = 'WhiteUnderbase',
  EmptyImageBox = 'EmptyImageBox',
  DtfImageBackground = 'DtfImageBackground',
  MaxCharCountReached = 'MaxCharCountReached',
  // GAPI-53417: part of the artwork sticks out of the gang sheet. Distinct from
  // NotVisible, which only fires once three quarters of an element has left the area.
  OutsideSheet = 'OutsideSheet',
  // Some of the element is off the print area and will be cut off; NotVisible takes over
  // once most of it is off. Off unless the host turns it on.
  PartlyOutside = 'PartlyOutside',
  // GAPI-61892: the text of a fixed-size frame (extra.fixedFrame) needs more height
  // than the frame has.
  TextOverflow = 'TextOverflow',
  // Print checks a print partner turns on; the sizes come from hostSettings.printTechRules.
  // Partly transparent pixels in a DTF image print as a faint halo or not at all.
  SemiTransparentPixels = 'SemiTransparentPixels',
  // Parts of an image thinner than the technology's minimum line width.
  FineDetail = 'FineDetail',
  SmallText = 'SmallText',
  ThinLine = 'ThinLine',
  // Colour gradients in an image on an embroidered area, which thread cannot reproduce.
  Gradient = 'Gradient',
}

interface ExportedElementWarningBase {
  type: WarningType;
  elementAddress: ElementAddress;
}

interface ExportedSpreadWarningBase {
  type: WarningType;
  spreadIndex: number;
}

export interface ExportedDPIWarning extends ExportedElementWarningBase {
  type: WarningType.LowDPI;
  dpi: number;
}

export interface ExportedNotVisibleWarning extends ExportedElementWarningBase {
  type: WarningType.NotVisible;
}

export interface ExportedPartlyOutsideWarning extends ExportedElementWarningBase {
  type: WarningType.PartlyOutside;
}

export interface ExportedOutsideSheetWarning extends ExportedElementWarningBase {
  type: WarningType.OutsideSheet;
}

export interface ExportedOutOfRangeWarning extends ExportedElementWarningBase {
  type: WarningType.OutOfRange;
}

export interface ExportedSampleElementNotReplacedWarning extends ExportedElementWarningBase {
  type: WarningType.SampleElementNotReplaced;
}

export interface ExportedDtfImageBackgroundWarning extends ExportedElementWarningBase {
  type: WarningType.DtfImageBackground;
}

export interface ExportedMaxCharCountReachedWarning extends ExportedElementWarningBase {
  type: WarningType.MaxCharCountReached;
}

export interface ExportedTextOverflowWarning extends ExportedElementWarningBase {
  type: WarningType.TextOverflow;
}

export interface ExportedSemiTransparentPixelsWarning extends ExportedElementWarningBase {
  type: WarningType.SemiTransparentPixels;
}

export interface ExportedFineDetailWarning extends ExportedElementWarningBase {
  type: WarningType.FineDetail;
  minLineWidthMm: number;
}

export interface ExportedSmallTextWarning extends ExportedElementWarningBase {
  type: WarningType.SmallText;
  textHeightMm: number;
  minTextHeightMm: number;
}

export interface ExportedThinLineWarning extends ExportedElementWarningBase {
  type: WarningType.ThinLine;
  lineWidthMm: number;
  minLineWidthMm: number;
}

export interface ExportedGradientWarning extends ExportedElementWarningBase {
  type: WarningType.Gradient;
}

export interface ExportedMaxStitchCountWarning extends ExportedSpreadWarningBase {
  type: WarningType.MaxStitchCount;
}

export interface ExportedMaxColorCountWarning extends ExportedSpreadWarningBase {
  type: WarningType.MaxColorCount;
}

export interface ExportedEmptyPageWarning extends ExportedSpreadWarningBase {
  type: WarningType.EmptyPage;
}

export interface ExportedEmptyDesignWarning {
  type: WarningType.EmptyDesign;
}

export interface ExportedWhiteUnderbaseWarning {
  type: WarningType.WhiteUnderbase;
}

export interface ExportedEmptyImageBoxWarning extends ExportedSpreadWarningBase {
  type: WarningType.EmptyImageBox;
}

export type ExportedWarning =
  | ExportedDPIWarning
  | ExportedNotVisibleWarning
  | ExportedPartlyOutsideWarning
  | ExportedOutOfRangeWarning
  | ExportedMaxStitchCountWarning
  | ExportedMaxColorCountWarning
  | ExportedSampleElementNotReplacedWarning
  | ExportedEmptyPageWarning
  | ExportedEmptyDesignWarning
  | ExportedEmptyImageBoxWarning
  | ExportedWhiteUnderbaseWarning
  | ExportedDtfImageBackgroundWarning
  | ExportedMaxCharCountReachedWarning
  | ExportedOutsideSheetWarning
  | ExportedTextOverflowWarning
  | ExportedSemiTransparentPixelsWarning
  | ExportedFineDetailWarning
  | ExportedSmallTextWarning
  | ExportedThinLineWarning
  | ExportedGradientWarning;

export type ExtendedWarning =
  | (DPIWarning & ExportedDPIWarning)
  | (NotVisibleWarning & ExportedNotVisibleWarning)
  | (PartlyOutsideWarning & ExportedPartlyOutsideWarning)
  | (OutOfRangeWarning & ExportedOutOfRangeWarning)
  | (SampleElementNotReplacedWarning & ExportedSampleElementNotReplacedWarning)
  | (MaxStitchCountWarning & ExportedMaxStitchCountWarning)
  | (MaxColorCountWarning & ExportedMaxColorCountWarning)
  | (EmptyPageWarning & ExportedEmptyPageWarning)
  | (EmptyDesignWarning & ExportedEmptyDesignWarning)
  | (EmptyImageBoxWarning & ExportedEmptyImageBoxWarning)
  | (DtfImageBackgroundWarning & ExportedDtfImageBackgroundWarning)
  | (WhiteUnderbaseWarning & ExportedWhiteUnderbaseWarning)
  | (MaxCharCountReachedWarning & ExportedMaxCharCountReachedWarning)
  | (OutsideSheetWarning & ExportedOutsideSheetWarning)
  | (TextOverflowWarning & ExportedTextOverflowWarning)
  | (SemiTransparentPixelsWarning & ExportedSemiTransparentPixelsWarning)
  | (FineDetailWarning & ExportedFineDetailWarning)
  | (SmallTextWarning & ExportedSmallTextWarning)
  | (ThinLineWarning & ExportedThinLineWarning)
  | (GradientWarning & ExportedGradientWarning);

const elementWarningTypeSet = new Set([
  WarningType.LowDPI,
  WarningType.NotVisible,
  WarningType.PartlyOutside,
  WarningType.OutOfRange,
  WarningType.SampleElementNotReplaced,
  WarningType.DtfImageBackground,
  WarningType.MaxCharCountReached,
  WarningType.OutsideSheet,
  WarningType.TextOverflow,
  WarningType.SemiTransparentPixels,
  WarningType.FineDetail,
  WarningType.SmallText,
  WarningType.ThinLine,
  WarningType.Gradient,
]);
const spreadWarningTypeSet = new Set([
  WarningType.MaxColorCount,
  WarningType.MaxStitchCount,
  WarningType.EmptyPage,
  WarningType.EmptyImageBox,
]);
const globalWarningTypeSet = new Set([WarningType.EmptyDesign]);
